import React from "react"

import Badge from "src/components/Badge"
import Tooltip from "src/components/Tooltip"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"

import JobLineItemNoteSubRow from "./JobLineItemNoteSubRow"
import { pricePrecisionDigits } from "./pricePrecision"

const getTypeIconClass = (productType) =>
  productType === "labor" ? "fa-clock-far" : "fa-package"

const InactiveBadge = () => (
  <span className="ml-2">
    <Badge color="red" text="Inactive" variant="small" />
  </span>
)

export const buildJobLineItemColumns = ({
  presentmentCurrency,
  templateVariant = false,
  onEditNote,
}) => [
  {
    key: "productName",
    header: "Description",
    width: "300px",
    render: (row) => {
      const productType = row.product?.productType
      const typeLabel = productType === "labor" ? "Labor" : "Parts"
      const isInactive = row.product?.active === false
      return (
        <span className="flex items-center">
          <Tooltip text={typeLabel} placement="top" variant="dark">
            <i
              className={`fa-kit ${getTypeIconClass(
                productType
              )} text-gray-500`}
              aria-label={typeLabel}
            />
          </Tooltip>
          <span className="ml-2 min-w-0 truncate">{row.product?.name}</span>
          {isInactive && <InactiveBadge />}
        </span>
      )
    },
    renderSubRow: (row) => {
      if (!row.notes || !row.notes.trim()) return null
      if (!onEditNote) {
        return (
          <div className="border-l-2 border-gray-300 pl-2.5 text-sm italic text-gray-600">
            {row.notes}
          </div>
        )
      }
      return (
        <JobLineItemNoteSubRow
          notes={row.notes}
          productName={row.product?.name}
          onClick={() => onEditNote(row)}
        />
      )
    },
  },
  {
    key: "pricePerUnit",
    header: "Rate",
    render: (row) => {
      // A price-at-sale catalog item carries no price, so a template line item
      // pointing at one has no rate to format.
      if (row.pricePerUnit == null) return "—"

      const precision = pricePrecisionDigits(row.product?.pricePrecision)
      const formatted = formatCurrencyFromMinorUnits({
        amount: row.pricePerUnit,
        currency: presentmentCurrency,
        precision,
      })
      const label = row.product?.rateUnitLabel
      return label ? `${formatted}${label}` : formatted
    },
  },
  {
    key: "quantity",
    header: "Qty",
    width: "100px",
    render: (row) => {
      if (templateVariant && row.pricingStructure === "by_length")
        return (
          <span className="italic text-gray-500">{`auto · by ${row.product.quantityUnitLabel}`}</span>
        )
      const qty = parseFloat(row.quantity)
      const label = row.product?.quantityUnitLabel
      return label ? `${qty} ${label}` : String(qty)
    },
  },
  {
    key: "subtotal",
    header: "Subtotal",
    cellAlign: "right",
    headerAlign: "right",
    render: (row) => {
      if (row.subtotal === null) return "—"
      return (
        <span className="font-semibold">
          {formatCurrencyFromMinorUnits({
            amount: row.subtotal,
            currency: presentmentCurrency,
            precision: 2,
          })}
        </span>
      )
    },
  },
]
